/**
 * Shared client-side helper for kicking off the Google Analytics 4 OAuth 2.0
 * flow.
 *
 * Used by:
 *   - IntegrationsPage's "Connect with Google" / "Reconnect" buttons on the
 *     GA4 card.
 *
 * Reads `VITE_GOOGLE_CLIENT_ID` from the build-time env. Stores a CSRF state
 * token in `sessionStorage` and redirects the browser to Google's OAuth
 * authorize endpoint. The matching state is validated by `Ga4CallbackPage`
 * after Google redirects back.
 *
 * Why we replaced the service-account JSON-key flow:
 *   Google's `iam.disableServiceAccountKeyCreation` org policy now blocks
 *   the JSON download for any project under an enrolled organization.
 *   3-legged user OAuth is the supported alternative and never touches a
 *   service account at all. See ROADMAP §2.1.
 */

export const GA4_OAUTH_STATE_KEY = 'ga4_oauth_state';

export const GA4_REDIRECT_PATH = '/integrations/ga4/callback';

// Minimum scope set we need:
//   analytics.readonly - call runReport / accountSummaries on GA4 properties.
// We deliberately do NOT request profile/email — Atlas already knows who
// the operator is via Supabase Auth, so the consent screen stays minimal.
export const GA4_OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/analytics.readonly',
] as const;

/**
 * Returns the redirect_uri that Google will send the user back to after
 * they grant or deny consent. This MUST be registered in the Google Cloud
 * Console under "Authorized redirect URIs" for the OAuth client and MUST
 * match exactly the value sent in both the auth request and the
 * token-exchange request.
 */
export function ga4RedirectUri(): string {
  return `${window.location.origin}${GA4_REDIRECT_PATH}`;
}

/**
 * Begins the OAuth flow by redirecting the browser to Google. Returns
 * `null` on success (the caller's tab is about to navigate away) and a
 * human-readable error string if the env isn't configured. Callers should
 * surface the error via toast/alert.
 *
 * Why `prompt=consent` + `access_type=offline`:
 *   Without `prompt=consent`, Google silently omits the refresh token if
 *   this user has previously approved the same client+scope combination,
 *   which would silently break our hourly cron sync. Forcing the consent
 *   screen every time guarantees we always receive a refresh token; the
 *   user only sees the screen on (re)connect, never during normal use.
 */
export function startGa4OAuth(): string | null {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    return 'VITE_GOOGLE_CLIENT_ID is not set. Add it to .env (it must match the GOOGLE_CLIENT_ID server secret) and redeploy.';
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(GA4_OAUTH_STATE_KEY, state);

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', GA4_OAUTH_SCOPES.join(' '));
  url.searchParams.set('redirect_uri', ga4RedirectUri());
  url.searchParams.set('state', state);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');
  // include_granted_scopes lets Google merge any prior scopes from this
  // client onto the new grant — harmless here since we only ever ask for
  // analytics.readonly, but keeps us consistent with Google's recommended
  // OAuth params.
  url.searchParams.set('include_granted_scopes', 'true');
  window.location.href = url.toString();
  return null;
}
